import { forkJoin, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CreateReleaseRequest, Release, UpdateReleaseRequest } from '../../shared/models/release.model';
import { ReleaseService } from '../../core/services/release.service';

export type ReleaseViewMode = 'releases' | 'downloads';

export const RELEASE_VIEW_OPTIONS = [
  { label: 'Releases', value: 'releases' as ReleaseViewMode, icon: 'pi pi-upload' },
  { label: 'Downloads History', value: 'downloads' as ReleaseViewMode, icon: 'pi pi-download' }
];

export function parseVersion(v: string): number[] {
  const clean = (v || '').split('-')[0].trim();
  return clean.split('.').map(p => parseInt(p, 10) || 0);
}

export function isHigherVersion(v1: string, v2: string): boolean {
  const parts1 = parseVersion(v1);
  const parts2 = parseVersion(v2);
  const maxLen = Math.max(parts1.length, parts2.length);
  for (let i = 0; i < maxLen; i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return true;
    if (p1 < p2) return false;
  }
  return false;
}

export function extractVersionFromFilename(filename: string): string | null {
  const versionPattern = /(\d+\.\d+\.\d+(?:\.\d+)?(?:-[a-zA-Z0-9.-]+)?)/;
  const match = filename.match(versionPattern);
  return match ? match[1] : null;
}

export function isValidVersionFormat(version: string): boolean {
  return /^\d+\.\d+\.\d+(?:\.\d+)?(-[a-zA-Z0-9.-]+)?$/.test(version);
}

export function isValidInstallerFileType(file: File): boolean {
  const allowedExtensions = ['.msi', '.exe'];
  const fileName = file.name.toLowerCase();
  return allowedExtensions.some(ext => fileName.endsWith(ext));
}

export function validateInstallerUpload(file: File | null): string | null {
  if (!file) return 'Please select a file to upload';
  if (!isValidInstallerFileType(file)) return 'Only MSI and EXE files are allowed.';
  if (file.size > 100 * 1024 * 1024) return 'File size must be less than 100MB.';
  return null;
}

export function emptyCreateReleaseForm(): CreateReleaseRequest {
  return {
    version: '',
    releaseNotes: '',
    fileName: '',
    fileUrl: '',
    fileSize: 0
  };
}

export function emptyUpdateReleaseForm(): UpdateReleaseRequest {
  return {
    id: '',
    version: '',
    releaseNotes: '',
    rowVersion: ''
  };
}

export function editFormForRelease(release: Release): UpdateReleaseRequest {
  return {
    id: release.id,
    version: release.version,
    releaseNotes: release.releaseNotes,
    rowVersion: release.rowVersion
  };
}

export function buildVersionFilterOptions(releases: Release[]): { label: string; value: string | null }[] {
  return [
    { label: 'All Versions', value: null },
    ...releases.map(r => ({ label: `v${r.version}`, value: r.version }))
  ];
}

export function getHighestPublishedRelease(releases: Release[]): Release | null {
  const published = releases.filter(r => r.isPublished);
  if (published.length === 0) return null;
  return published.reduce((prev, curr) =>
    isHigherVersion(curr.version, prev.version) ? curr : prev
  );
}

export function canRollbackToRelease(release: Release, releases: Release[]): boolean {
  const highestPublished = getHighestPublishedRelease(releases);
  if (!highestPublished) return false;
  return highestPublished.id !== release.id && isHigherVersion(highestPublished.version, release.version);
}

export function buildRollbackStream(
  release: Release,
  releases: Release[],
  releaseService: ReleaseService
): Observable<unknown> {
  const higherPublished = releases.filter(
    r => r.isPublished && r.id !== release.id && isHigherVersion(r.version, release.version)
  );
  const unpublishTasks = higherPublished.map(r => releaseService.unpublishRelease(r.id));
  const publishTarget = releaseService.publishRelease(release.id);

  return higherPublished.length > 0
    ? forkJoin(unpublishTasks).pipe(switchMap(() => publishTarget))
    : publishTarget;
}

export function getReleaseStatusSeverity(isPublished: boolean): 'success' | 'warn' {
  return isPublished ? 'success' : 'warn';
}

export function getReleaseStatusText(isPublished: boolean): string {
  return isPublished ? 'Published' : 'Unpublished';
}

export function describeReleaseError(error: any, fallback: string): string {
  if (!error) return fallback;
  return error.error?.detail || error.error?.message || (Array.isArray(error.error?.errors) ? error.error.errors.join(', ') : fallback);
}

export function validateCreateReleaseForm(form: CreateReleaseRequest, uploadedFile: File | null): string | null {
  const uploadError = validateInstallerUpload(uploadedFile);
  if (uploadError) return uploadError;

  const version = form.version?.trim();
  if (!version) return 'Version is required. Please specify version (e.g., 2.0.0)';
  if (!isValidVersionFormat(version)) return 'Version must be in format major.minor.patch (e.g., 2.0.0 or 2.0.0.0)';
  if (!form.releaseNotes?.trim()) return 'Release Notes are required';
  return null;
}

export function buildCreateReleaseFormData(form: CreateReleaseRequest, uploadedFile: File | null): FormData {
  const formData = new FormData();
  formData.append('version', form.version.trim());
  formData.append('releaseNotes', form.releaseNotes || '');
  if (uploadedFile) {
    formData.append('file', uploadedFile);
  }
  return formData;
}

export function triggerReleaseDownload(fileUrl: string, fileName?: string, version?: string): void {
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = fileName || `release-${version || 'installer'}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
